import { API_URL } from '../config/aws-config';
import { fetchAuthSession } from 'aws-amplify/auth';

async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };
}

export interface Child {
  student_id: string;
  name: string;
  consent_given: boolean;
  is_premium: boolean;
  credits_remaining: number;
  linked_at: string;
}

export interface ChildStats {
  student_id: string;
  name: string;
  stats: {
    ai_interactions_total: number;
    ai_interactions_last_7_days: number;
    study_sessions_total: number;
    study_minutes_total: number;
    credits_remaining: number;
    is_premium: boolean;
    course_sheets_count: number;
    quiz_count: number;
  };
  consent_given: boolean;
}

export async function linkChild(code: string): Promise<{ student: { student_id: string; name: string } }> {
  const response = await fetch(`${API_URL}/children/link`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Erreur lors de la liaison');
  }

  return response.json();
}

export async function listChildren(): Promise<Child[]> {
  const response = await fetch(`${API_URL}/children`, {
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des enfants');
  }

  const data = await response.json();
  return data.children;
}

export async function getChildStats(studentId: string): Promise<ChildStats> {
  const response = await fetch(`${API_URL}/children/${studentId}/stats`, {
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des stats');
  }

  return response.json();
}

export async function giveConsent(studentId: string): Promise<void> {
  const response = await fetch(`${API_URL}/children/${studentId}/consent`, {
    method: 'POST',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Erreur lors de l\'enregistrement du consentement');
  }
}

export async function createCheckoutSession(studentId: string): Promise<{ checkout_url: string }> {
  const response = await fetch(`${API_URL}/subscription/create-checkout`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      student_id: studentId,
      return_url: window.location.origin + '/upgrade/' + studentId,
    }),
  });

  if (!response.ok) {
    throw new Error('Erreur lors de la création de la session de paiement');
  }

  return response.json();
}

export async function getSubscriptionStatus(studentId: string): Promise<{
  is_premium: boolean;
  credits_remaining: number;
}> {
  const response = await fetch(`${API_URL}/subscription/status?student_id=${studentId}`, {
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Erreur lors de la récupération du statut');
  }

  return response.json();
}

// ========================================
// Parent AI Coach
// ========================================

export interface CoachMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface CoachResponse {
  response: string;
  remaining_messages: number;
  daily_limit: number;
}

export async function askParentCoach(studentId: string, message: string): Promise<CoachResponse> {
  const response = await fetch(`${API_URL}/children/${studentId}/coach`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ message }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Erreur lors de la communication avec le coach');
  }

  return response.json();
}

export async function getCoachHistory(studentId: string): Promise<CoachMessage[]> {
  const response = await fetch(`${API_URL}/children/${studentId}/coach/history`, {
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Erreur lors de la récupération de l\'historique');
  }

  const data = await response.json();
  return data.messages;
}

